// <bundle-tabs> — accessible tab behavior + add-to-cart bridge
class BundleTabs extends HTMLElement {
  constructor() {
    super();
    this.onTabClick = this.onTabClick.bind(this);
    this.onTabKeydown = this.onTabKeydown.bind(this);
    this.onClick = this.onClick.bind(this);
  }

  connectedCallback() {
    // Elements
    this.tablist = this.querySelector('[role="tablist"]');
    this.tabs = Array.from(this.querySelectorAll('[role="tab"]'));
    this.panels = Array.from(this.querySelectorAll('[role="tabpanel"]'));

    if (!this.tablist || this.tabs.length === 0 || this.panels.length === 0) return;

    // Ensure a single selected tab on init
    const selectedIndex = Math.max(
      0,
      this.tabs.findIndex((t) => t.getAttribute('aria-selected') === 'true')
    );
    this.tabs.forEach((tab, i) => {
      tab.setAttribute('tabindex', i === selectedIndex ? '0' : '-1');
    });
    this.sync(selectedIndex, { focus: false, announce: false });

    // Events
    this.tablist.addEventListener('click', this.onTabClick);
    this.tablist.addEventListener('keydown', this.onTabKeydown);
    this.addEventListener('click', this.onClick);
  }

  disconnectedCallback() {
    if (this.tablist) {
      this.tablist.removeEventListener('click', this.onTabClick);
      this.tablist.removeEventListener('keydown', this.onTabKeydown);
    }
    this.removeEventListener('click', this.onClick);
  }

  // ----- Tab interactions -----
  onTabClick(e) {
    const btn = e.target.closest('[role="tab"]');
    if (!btn) return;
    const index = this.tabs.indexOf(btn);
    if (index === -1) return;
    this.sync(index, { focus: true, announce: true });
  }

  onTabKeydown(e) {
    const current = e.target.closest('[role="tab"]');
    if (!current) return;

    const idx = this.tabs.indexOf(current);
    if (idx === -1) return;

    let nextIndex = null;

    switch (e.key) {
      case 'ArrowRight':
      case 'ArrowDown':
        nextIndex = (idx + 1) % this.tabs.length;
        break;
      case 'ArrowLeft':
      case 'ArrowUp':
        nextIndex = (idx - 1 + this.tabs.length) % this.tabs.length;
        break;
      case 'Home':
        nextIndex = 0;
        break;
      case 'End':
        nextIndex = this.tabs.length - 1;
        break;
      case 'Enter':
      case ' ':
        this.sync(idx, { focus: true, announce: true });
        e.preventDefault();
        return;
      default:
        return;
    }

    if (nextIndex != null) {
      e.preventDefault();
      this.focusTab(nextIndex);
    }
  }

  focusTab(index) {
    this.tabs.forEach((t, i) => t.setAttribute('tabindex', i === index ? '0' : '-1'));
    this.tabs[index].focus();
  }

  sync(activeIndex, { focus = false, announce = true } = {}) {
    // Tabs
    this.tabs.forEach((tab, i) => {
      const selected = i === activeIndex;
      tab.setAttribute('aria-selected', String(selected));
      tab.setAttribute('tabindex', selected ? '0' : '-1');
    });

    // Panels
    this.panels.forEach((panel, i) => {
      if (i === activeIndex) {
        panel.removeAttribute('hidden');
      } else {
        panel.setAttribute('hidden', '');
      }
    });

    if (focus) this.tabs[activeIndex].focus();

  }

  // ----- Add to cart (delegated) -----
  async onClick(e) {
    const btn = e.target.closest('[data-action="add-to-cart"]');
    if (!btn) return;

    const variantId = btn.getAttribute('data-variant-id');
    if (!variantId) return;

    btn.disabled = true;

    try {
      const item = { id: Number(variantId), quantity: 1 };
      const cartResult = window.CartUtils
        ? await window.CartUtils.addToCart(item)
        : await fetch('/cart/add.js', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
            body: JSON.stringify({ items: [item] }),
          }).then(async (res) => {
            if (!res.ok) throw new Error('Cart add failed');
            return res.json();
          });

      const addedItem = window.CartUtils
        ? cartResult.items?.find((entry) => String(entry.variant_id) === String(variantId))
        : cartResult;

      // Announce to screen reader
      if (window.announceToScreenReader && addedItem) {
        window.announceToScreenReader(`${addedItem.product_title} added to cart`);
      }

      // Also request a cart refresh for components that depend on it
      this.dispatchEvent(
        new CustomEvent('cart:refresh', {
          bubbles: true,
          detail: { source: 'bundle-tabs' }
        })
      );

      // Dispatch cart updated event for progress bar updates
      document.dispatchEvent(new CustomEvent('cart:updated'));

    } catch (err) {
      console.error(err);
      
      // Announce error to screen reader
      if (window.announceToScreenReader) {
        window.announceToScreenReader(`Error: ${err?.message || 'Failed to add item to cart'}`, 'assertive');
      }
      
      this.dispatchEvent(
        new CustomEvent('cart:error', {
          bubbles: true,
          detail: { message: err?.message || 'Cart error', source: 'bundle-tabs' }
        })
      );
    } finally {
      btn.disabled = false;
    }
  }

}

// Define once
if (!customElements.get('bundle-tabs')) {
  customElements.define('bundle-tabs', BundleTabs);
}
